/**
 * ComplianceDetail.tsx — Compliance framework detail page (Layer 4).
 *
 * Shows controls for a selected framework, pass/fail status, evidence counts,
 * and a compliance score trend chart.
 */

import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import API_BASE from '../config/api';
import { useAuth } from '../context/AuthContext';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Skeleton from '@mui/material/Skeleton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DownloadIcon from '@mui/icons-material/Download';
import ErrorIcon from '@mui/icons-material/Error';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

import { LineChart } from '@mui/x-charts/LineChart';

// ── Types ─────────────────────────────────────────────────────────────────────

interface Control {
  id: string;
  control_id: string;
  control_name: string;
  description: string;
  category: string;
  prowler_check_ids: string[];
}

interface Assessment {
  id: string;
  overall_score: number;
  status: string;
  total_controls: number;
  passed_controls: number;
  failed_controls: number;
  assessment_date: string;
}

interface Evidence {
  id: string;
  control_id: string;
  evidence_type: string;
  evidence_data: Record<string, any>;
  collected_at: string;
}

interface TimelinePoint {
  date: string;
  score: number;
  status: string;
  passed: number;
  failed: number;
  total: number;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function scoreColor(score: number): string {
  if (score >= 80) return '#2ea55e';
  if (score >= 60) return '#f2a71e';
  return '#d43e3e';
}

function StatusIcon({ status }: { status: 'pass' | 'fail' | 'unknown' }) {
  if (status === 'pass')    return <CheckCircleIcon sx={{ color: '#2ea55e', fontSize: 20 }} />;
  if (status === 'fail')    return <ErrorIcon sx={{ color: '#d43e3e', fontSize: 20 }} />;
  return <HelpOutlineIcon sx={{ color: 'text.disabled', fontSize: 20 }} />;
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function ComplianceDetail() {
  const { framework } = useParams<{ framework: string }>();
  const frameworkName  = (framework ?? '').toUpperCase();
  const { user }       = useAuth();
  const navigate       = useNavigate();

  const orgId = user?.customer_id;

  const [controls,   setControls]   = useState<Control[]>([]);
  const [assessment, setAssessment] = useState<Assessment | null>(null);
  const [evidence,   setEvidence]   = useState<Evidence[]>([]);
  const [timeline,   setTimeline]   = useState<TimelinePoint[]>([]);
  const [loading,    setLoading]    = useState(true);
  const [running,    setRunning]    = useState(false);
  const [expanded,   setExpanded]   = useState<Record<string, boolean>>({});
  const [error,      setError]      = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Load controls always
      const ctrlRes = await axios.get(`${API_BASE}/v1/compliance/controls/${frameworkName}`);
      setControls(ctrlRes.data);

      // Load assessment + evidence + timeline if we have an org
      if (orgId) {
        const [assessRes, timelineRes] = await Promise.allSettled([
          axios.get(`${API_BASE}/v1/compliance/assessment/${orgId}/${frameworkName}`),
          axios.get(`${API_BASE}/v1/compliance/timeline/${orgId}/${frameworkName}?months=12`),
        ]);

        if (assessRes.status === 'fulfilled') {
          const a = assessRes.value.data;
          setAssessment(a);
          // Load evidence
          try {
            const evRes = await axios.get(`${API_BASE}/v1/compliance/evidence/${a.id}`);
            setEvidence(evRes.data);
          } catch { /* no evidence yet */ }
        }

        if (timelineRes.status === 'fulfilled') {
          setTimeline(timelineRes.value.data.timeline ?? []);
        }
      }
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to load compliance data');
    } finally {
      setLoading(false);
    }
  }, [frameworkName, orgId]);

  useEffect(() => { load(); }, [load]);

  async function runAssessment() {
    if (!orgId) return;
    setRunning(true);
    try {
      await axios.post(`${API_BASE}/v1/compliance/assess/${orgId}/${frameworkName}`);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Assessment failed');
    } finally {
      setRunning(false);
    }
  }

  async function downloadReport() {
    if (!assessment) return;
    try {
      const res = await axios.get(
        `${API_BASE}/v1/compliance/report/${assessment.id}/pdf`,
        { responseType: 'blob' }
      );
      const url  = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href     = url;
      link.download = `compliance-${frameworkName}-${new Date().toISOString().slice(0, 10)}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      setError('Failed to download report');
    }
  }

  // Build evidence lookup by control_id
  const evidenceByControl: Record<string, Evidence[]> = {};
  for (const ev of evidence) {
    (evidenceByControl[ev.control_id] ??= []).push(ev);
  }

  // Determine control statuses from evidence
  function controlStatus(ctrl: Control): 'pass' | 'fail' | 'unknown' {
    const evs = evidenceByControl[ctrl.id] ?? [];
    if (evs.length === 0) return 'unknown';
    const hasFail = evs.some((e) =>
      ['critical', 'high', 'medium'].includes(e.evidence_data?.severity ?? '')
    );
    return hasFail ? 'fail' : 'pass';
  }

  if (loading) {
    return (
      <Box>
        <Skeleton height={40} width={300} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={120} sx={{ mb: 2, borderRadius: 2 }} />
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Back + header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <IconButton onClick={() => navigate('/compliance')} size="small">
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={700}>{frameworkName} Compliance</Typography>
          <Typography variant="body2" color="text.secondary">
            Control-level status and evidence
          </Typography>
        </Box>
        {orgId && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              startIcon={running ? <CircularProgress size={14} color="inherit" /> : <PlayArrowIcon />}
              disabled={running}
              onClick={runAssessment}
              size="small"
            >
              {running ? 'Running…' : 'Run Assessment'}
            </Button>
            {assessment && (
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={downloadReport}
                size="small"
              >
                Download PDF
              </Button>
            )}
          </Box>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Score summary */}
      {assessment && (
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          {[
            { label: 'Score', value: `${assessment.overall_score}%`, color: scoreColor(assessment.overall_score) },
            { label: 'Status', value: assessment.status.replace(/_/g, ' '), color: undefined },
            { label: 'Passed', value: `${assessment.passed_controls} controls`, color: '#2ea55e' },
            { label: 'Failed', value: `${assessment.failed_controls} controls`, color: '#d43e3e' },
            { label: 'Total',  value: `${assessment.total_controls} controls`,  color: undefined },
          ].map(({ label, value, color }) => (
            <Card key={label} elevation={1} sx={{ minWidth: 120 }}>
              <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="caption" color="text.secondary">{label}</Typography>
                <Typography variant="h6" fontWeight={700} sx={{ color: color ?? 'text.primary' }}>
                  {value}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Compliance score trend chart */}
      {timeline.length > 1 && (
        <Card elevation={1} sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Compliance Score Trend (12 months)
            </Typography>
            <LineChart
              xAxis={[{
                data: timeline.map((_, i) => i),
                valueFormatter: (i: number) => timeline[i]?.date?.slice(0, 10) ?? '',
              }]}
              series={[{
                data: timeline.map((p) => p.score),
                label: 'Score',
                color: '#1976d2',
                area: true,
              }]}
              height={200}
              margin={{ top: 10, bottom: 30, left: 40, right: 20 }}
            />
          </CardContent>
        </Card>
      )}

      {/* Controls table */}
      <TableContainer component={Paper} elevation={1}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 700 }}>Control</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="center">Status</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="center">Evidence</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="center">Checks</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {controls.map((ctrl) => {
              const status   = controlStatus(ctrl);
              const evs      = evidenceByControl[ctrl.id] ?? [];
              const isOpen   = expanded[ctrl.id] ?? false;

              return (
                <React.Fragment key={ctrl.id}>
                  <TableRow
                    hover
                    sx={{ '& > *': { borderBottom: isOpen ? 0 : undefined } }}
                  >
                    <TableCell>
                      <Chip label={ctrl.control_id} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{ctrl.control_name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">{ctrl.category}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                        <StatusIcon status={status} />
                        <Typography variant="caption" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                          {status}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={evs.length} size="small" color={evs.length > 0 ? 'primary' : 'default'} />
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="caption" color="text.secondary">
                        {ctrl.prowler_check_ids.length}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => setExpanded((prev) => ({ ...prev, [ctrl.id]: !isOpen }))}
                      >
                        {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </TableCell>
                  </TableRow>

                  {/* Expandable row: description + prowler checks + evidence */}
                  <TableRow>
                    <TableCell colSpan={7} sx={{ py: 0 }}>
                      <Collapse in={isOpen} unmountOnExit>
                        <Box sx={{ py: 2, px: 3, bgcolor: 'action.hover' }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                            {ctrl.description}
                          </Typography>

                          <Typography variant="caption" fontWeight={600} color="text.secondary">
                            Prowler Checks ({ctrl.prowler_check_ids.length})
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5, mb: 1.5 }}>
                            {ctrl.prowler_check_ids.map((id) => (
                              <Chip key={id} label={id} size="small" sx={{ fontFamily: 'monospace', fontSize: 10 }} />
                            ))}
                          </Box>

                          {evs.length > 0 && (
                            <>
                              <Typography variant="caption" fontWeight={600} color="text.secondary">
                                Evidence Records ({evs.length})
                              </Typography>
                              {evs.slice(0, 5).map((ev) => (
                                <Box key={ev.id} sx={{ mt: 0.5, p: 1, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                                  <Typography variant="caption" color="text.secondary">
                                    {new Date(ev.collected_at).toLocaleString()} — {ev.evidence_type}
                                  </Typography>
                                  {ev.evidence_data?.title && (
                                    <Typography variant="body2" fontWeight={500} noWrap>
                                      {ev.evidence_data.title}
                                    </Typography>
                                  )}
                                  {ev.evidence_data?.severity && (
                                    <Chip
                                      label={ev.evidence_data.severity}
                                      size="small"
                                      color={
                                        ev.evidence_data.severity === 'critical' ? 'error' :
                                        ev.evidence_data.severity === 'high' ? 'error' :
                                        ev.evidence_data.severity === 'medium' ? 'warning' : 'default'
                                      }
                                      sx={{ mt: 0.5 }}
                                    />
                                  )}
                                </Box>
                              ))}
                              {evs.length > 5 && (
                                <Typography variant="caption" color="text.secondary">
                                  + {evs.length - 5} more evidence records
                                </Typography>
                              )}
                            </>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
